"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getUserProfileAction,
  handleUserLoginAction,
} from "@/resources/actions/auth.actions";
import { queryKeys } from "@/lib/query-keys";

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface LoginError {
  message: string;
}

/**
 * Login mutation hook
 * Handles user authentication with proper error handling and cache invalidation
 */
export function useLoginMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      const result = await handleUserLoginAction(
        credentials.email,
        credentials.password,
        credentials.rememberMe ?? false,
      );

      if (result.error) {
        throw new Error(result.error);
      }

      return result.data;
    },
    onSuccess: async () => {
      // Invalidate auth-related queries to refetch user data
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.all });

      // Prefetch full user profile
      await queryClient.prefetchQuery({
        queryKey: queryKeys.auth.userProfile(),
        queryFn: getUserProfileAction,
      });
    },
    onError: (error: Error) => {
      // Error handling can be extended here (e.g., logging to error service)
      console.error("Login failed:", error.message);
    },
  });
}
